import React, { useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { PageContainer } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useFetchClientDetails, useFetchClientHealthMetrics } from '@/hooks/useClients';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { ProfileHeader } from '@/components/clients/profile/ProfileHeader';
import { RolesRatesCard } from '@/components/clients/profile/RolesRatesCard';
import { HealthRatingsStrip } from '@/components/clients/profile/HealthRatingsStrip';
import { ComputedMetricsRow } from '@/components/clients/profile/ComputedMetricsRow';
import { ActivityTab } from '@/components/clients/profile/ActivityTab';
import { BookingsTab } from '@/components/clients/profile/BookingsTab';
import { FinancialTab } from '@/components/clients/profile/FinancialTab';
import { NotesTab } from '@/components/clients/profile/NotesTab';
import { ConfigTab } from '@/components/clients/profile/ConfigTab';
import { ContactsTab } from '@/components/clients/profile/ContactsTab';
import { MobileSummary } from '@/components/clients/profile/MobileSummary';
import { QuickActions } from '@/components/clients/profile/QuickActions';

type ProfileTab = 'summary' | 'activity' | 'bookings' | 'financial' | 'notes' | 'config' | 'contacts';

const ClientProfilePage: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const [activeTab, setActiveTab] = useState<ProfileTab>(isDesktop ? 'activity' : 'summary');

  const { data: client, isLoading: isLoadingClient, error: clientError } = useFetchClientDetails(clientId ?? null);
  const { data: healthMetrics } = useFetchClientHealthMetrics(clientId ?? null);

  const handleBack = useCallback(() => {
    navigate('/clients');
  }, [navigate]);

  const handleNavigateToTab = useCallback((tab: ProfileTab) => {
    setActiveTab(tab);
    // On mobile, scroll to top when switching tabs
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const clientName = client?.name ?? 'Client';

  if (isLoadingClient) {
    return (
      <PageContainer title="Client Profile">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      </PageContainer>
    );
  }

  if (clientError || !client) {
    return (
      <PageContainer title="Client Profile">
        <div className="space-y-4">
          <Button variant="ghost" size="sm" onClick={handleBack} className="gap-1.5">
            <ArrowLeft className="h-4 w-4" />
            Back to Clients
          </Button>
          <div className="text-center py-12">
            <p className="text-destructive">
              {clientError ? `Error loading client: ${clientError.message}` : 'Client not found'}
            </p>
          </div>
        </div>
      </PageContainer>
    );
  }

  const roles = (client.roles as Array<{ id: string; name: string; day_rate?: number; hourly_rate?: number }>) ?? [];

  return (
    <PageContainer title={clientName} subtitle="Client Profile">
      <div className="space-y-4">
        {/* Back navigation */}
        <Button variant="ghost" size="sm" onClick={handleBack} className="gap-1.5 -ml-2">
          <ArrowLeft className="h-4 w-4" />
          Back to Clients
        </Button>

        {/* Profile Header */}
        <ProfileHeader client={client} healthMetrics={healthMetrics ?? null} />

        {/* Roles & Rates — always visible */}
        <RolesRatesCard roles={roles} />

        {/* Health Ratings Strip */}
        <HealthRatingsStrip healthMetrics={healthMetrics ?? null} />

        {/* Computed Metrics Row */}
        <ComputedMetricsRow healthMetrics={healthMetrics ?? null} />

        {/* Desktop: Tabs / Mobile: Summary or Tab Content */}
        {isDesktop ? (
          <Tabs
            value={activeTab === 'summary' ? 'activity' : activeTab}
            onValueChange={(v) => setActiveTab(v as ProfileTab)}
            className="w-full"
          >
            <TabsList className="w-full justify-start bg-surface-1 border border-border rounded overflow-x-auto scrollbar-none">
              <TabsTrigger value="activity" className="font-label uppercase tracking-wider text-xs">Activity</TabsTrigger>
              <TabsTrigger value="bookings" className="font-label uppercase tracking-wider text-xs">Bookings</TabsTrigger>
              <TabsTrigger value="financial" className="font-label uppercase tracking-wider text-xs">Financial</TabsTrigger>
              <TabsTrigger value="notes" className="font-label uppercase tracking-wider text-xs">Notes</TabsTrigger>
              <TabsTrigger value="contacts" className="font-label uppercase tracking-wider text-xs">Contacts</TabsTrigger>
              <TabsTrigger value="config" className="font-label uppercase tracking-wider text-xs">Config</TabsTrigger>
            </TabsList>
            <TabsContent value="activity" className="mt-4">
              {activeTab === 'activity' && <ActivityTab clientId={clientId!} />}
            </TabsContent>
            <TabsContent value="bookings" className="mt-4">
              {activeTab === 'bookings' && <BookingsTab clientId={clientId!} />}
            </TabsContent>
            <TabsContent value="financial" className="mt-4">
              {activeTab === 'financial' && <FinancialTab clientId={clientId!} healthMetrics={healthMetrics ?? null} />}
            </TabsContent>
            <TabsContent value="notes" className="mt-4">
              {activeTab === 'notes' && <NotesTab clientId={clientId!} />}
            </TabsContent>
            <TabsContent value="contacts" className="mt-4">
              {activeTab === 'contacts' && <ContactsTab clientId={clientId!} />}
            </TabsContent>
            <TabsContent value="config" className="mt-4">
              {activeTab === 'config' && <ConfigTab clientId={clientId!} client={client} />}
            </TabsContent>
          </Tabs>
        ) : (
          <div className="space-y-4 pb-20">
            {activeTab === 'summary' ? (
              <MobileSummary clientId={clientId!} onNavigateToTab={handleNavigateToTab} />
            ) : activeTab === 'activity' ? (
              <ActivityTab clientId={clientId!} />
            ) : activeTab === 'bookings' ? (
              <BookingsTab clientId={clientId!} />
            ) : activeTab === 'financial' ? (
              <FinancialTab clientId={clientId!} healthMetrics={healthMetrics ?? null} />
            ) : activeTab === 'notes' ? (
              <NotesTab clientId={clientId!} />
            ) : activeTab === 'contacts' ? (
              <ContactsTab clientId={clientId!} />
            ) : activeTab === 'config' ? (
              <ConfigTab clientId={clientId!} client={client} />
            ) : null}

            {/* Mobile tab navigation */}
            {activeTab !== 'summary' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setActiveTab('summary')}
                className="w-full"
              >
                Back to Summary
              </Button>
            )}
          </div>
        )}

        {/* Mobile quick actions */}
        {!isDesktop && <QuickActions clientId={clientId!} clientName={clientName} />}
      </div>
    </PageContainer>
  );
};

export default ClientProfilePage;
